import React from 'react';
import { CheckCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { format } from 'date-fns';

export default function FlaggedInteractionsPanel({ flaggedItems, onUpdateStatus, onRefresh }) {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApprove = async (flagId) => {
    const notes = prompt('Notes de revue (optionnel):');
    await onUpdateStatus(flagId, 'approved', notes || 'Approuvé');
  };

  const handleReject = async (flagId) => {
    const notes = prompt('Raison du rejet:');
    if (notes) {
      await onUpdateStatus(flagId, 'rejected', notes);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Signalements en Attente</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Actualiser
        </button>
      </div>
      {flaggedItems?.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <CheckCircle className="mx-auto text-green-600 mb-3" size={48} />
          <p className="text-gray-600">Aucun signalement en attente</p>
        </div>
      ) : (
        <div className="space-y-4">
          {flaggedItems?.map((flag) => (
            <div key={flag?.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(flag?.severity)}`}>
                      {flag?.severity === 'critical' && '🔴 Critique'}
                      {flag?.severity === 'high' && '🟠 Haute'}
                      {flag?.severity === 'medium' && '🟡 Moyenne'}
                      {flag?.severity === 'low' && '🔵 Faible'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(flag?.status)}`}>
                      {flag?.status === 'pending' && 'En attente'}
                      {flag?.status === 'reviewed' && 'Revu'}
                      {flag?.status === 'approved' && 'Approuvé'}
                      {flag?.status === 'rejected' && 'Rejeté'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(flag.created_at), 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">
                    Type: <span className="font-medium text-gray-900">{flag?.flag_type}</span>
                  </p>
                </div>
              </div>

              {/* Original Conversation */}
              {flag?.conversation_log && (
                <div className="mb-4 space-y-2">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium mb-1">Question Client:</p>
                    <p className="text-sm text-gray-900">{flag?.conversation_log?.user_message}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-600 font-medium mb-1">Réponse Fatou:</p>
                    <p className="text-sm text-gray-900">{flag?.conversation_log?.ai_response}</p>
                  </div>
                </div>
              )}

              {/* Flag Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-1">Description du Problème:</p>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{flag?.description}</p>
                </div>

                {flag?.suggested_correction && (
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-1">Correction Suggérée:</p>
                    <p className="text-sm text-gray-900 bg-green-50 p-3 rounded-lg border border-green-200">{flag?.suggested_correction}</p>
                  </div>
                )}

                {flag?.review_notes && (
                  <div>
                    <p className="text-xs text-gray-600 font-medium mb-1">Notes de Revue:</p>
                    <p className="text-sm text-gray-900 bg-blue-50 p-3 rounded-lg">{flag?.review_notes}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {flag?.status === 'pending' && (
                <div className="mt-4 flex items-center space-x-3">
                  <button
                    onClick={() => handleApprove(flag?.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <ThumbsUp size={16} />
                    <span>Approuver</span>
                  </button>
                  <button
                    onClick={() => handleReject(flag?.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <ThumbsDown size={16} />
                    <span>Rejeter</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}